import type { ReactNode } from "react";
import { createContext, useEffect, useMemo, useRef } from "react";

import { createT } from "./createT";
import { TranslationLookupRegistry } from "./TranslationLookupRegistry";
import type { Languages, TranslationFunction, TranslationOptions, Translations } from "./types";

export interface I18nContextValue<T extends Record<string, string> = Translations> {
  language: string;
  translations: T;
  t: TranslationFunction<T>;
  changeLanguage: (lang: string) => void;
  additionalOptions?: TranslationOptions;
  /** Runtime lookup evidence for inspector and release tooling; snapshots reset when language or catalog changes. */
  lookupRegistry: TranslationLookupRegistry;
}

export const I18nContext = createContext<I18nContextValue | undefined>(undefined);

interface I18nProviderProps<T extends Record<string, string> = Translations> {
  children: ReactNode;
  language: Languages;
  translations: T;
  onChangeLanguage?: (language: Languages) => void;
  onMissingKey?: (key: string, language: string) => void;
  onMissingVariable?: (key: string, value: string, language: string) => void;
  additionalOptions?: TranslationOptions;
}

export function I18nProvider<T extends Record<string, string> = Translations>({
  children,
  language,
  onChangeLanguage,
  translations,
  additionalOptions,
  onMissingKey,
  onMissingVariable,
}: I18nProviderProps<T>): React.ReactNode {
  const missingKeyRef = useRef(onMissingKey);
  const missingVariableRef = useRef(onMissingVariable);
  // Runtime evidence must be cleared when either the locale or its catalog changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const lookupRegistry = useMemo(() => new TranslationLookupRegistry(), [language, translations]);

  useEffect(() => {
    missingKeyRef.current = onMissingKey;
    missingVariableRef.current = onMissingVariable;
  }, [onMissingKey, onMissingVariable]);

  const t = useMemo(
    () =>
      createT({
        translations,
        language,
        onMissingKey: (key) => missingKeyRef.current?.(key, language),
        onMissingVariable: (key, value) => missingVariableRef.current?.(key, value, language),
        onLookup: lookupRegistry.record,
      }),
    [language, lookupRegistry, translations],
  );

  const contextValue = useMemo(
    () => ({
      language,
      translations,
      t,
      additionalOptions,
      lookupRegistry,
      changeLanguage: onChangeLanguage || (() => {}),
    }),
    [language, translations, t, additionalOptions, lookupRegistry, onChangeLanguage],
  );

  return <I18nContext value={contextValue as unknown as I18nContextValue}>{children}</I18nContext>;
}
